import { Injectable } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { EntityKeyByReference, GlobalEntityDefinition } from '@card-software/library';
import { BaseTransactionStrategy } from './base-transaction.strategy';
import { SupplyWarehouseDocumentType } from '../../models/supply-warehouse/supply-warehouse-document-type.enum';
import { TableColumn } from '../../models/supply-warehouse/table-column.model';
import { TransactionAction } from '../../models/supply-warehouse/transaction-action.model';
import { DispatchRequest, StatusDispatchRequest } from '../../models/dispatch-request/dispatch-request';
import { DispatchRequestProxyService } from '../../proxies/inventory/dispatch-request.proxy.service';

/**
 * Estrategia para manejar Dispatch Requests (DR)
 * El DR se edita directamente, no genera un documento hijo
 */
@Injectable({
  providedIn: 'root',
})
export class DispatchRequestStrategy extends BaseTransactionStrategy<DispatchRequest, StatusDispatchRequest> {
  readonly documentType = SupplyWarehouseDocumentType.DispatchRequest;
  readonly initiatorName = 'Dispatch Request';
  // eslint-disable-next-line @typescript-eslint/prefer-as-const
  readonly locationAssignmentType: 'source' = 'source';

  constructor(private dispatchRequestService: DispatchRequestProxyService) {
    super();
  }

  // ==================== Métodos de Carga ====================

  loadDocument(id: string): Observable<DispatchRequest | null> {
    return this.dispatchRequestService.getById(id).pipe(map((response) => response.data || null));
  }

  /**
   * Para Dispatch Request, el documento iniciador es el mismo documento
   * No hay un documento padre que lo genere
   */
  loadInitiatorDocument(dispatchRequestId: string): Observable<DispatchRequest | null> {
    return this.loadDocument(dispatchRequestId);
  }

  /**
   * Dispatch Request no tiene documentos de transacción hijos
   * Retorna array vacío
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  searchTransactionDocuments(dispatchRequestId: string): Observable<DispatchRequest[]> {
    return of([]);
  }

  /**
   * Dispatch Request no se crea desde otro documento en este contexto
   * Este método no se usa para DR
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  createFromInitiator(dispatchRequestId: string, locationCode: string): Observable<DispatchRequest> {
    throw new Error('Dispatch Request does not support createFromInitiator. Load the document directly.');
  }

  // ==================== Métodos de Actualización ====================

  updateDocument(id: string, document: DispatchRequest): Observable<DispatchRequest> {
    return this.dispatchRequestService.update(id, document).pipe(map((response) => response.data!));
  }

  updateStatus(id: string, newStatus: StatusDispatchRequest): Observable<DispatchRequest> {
    return this.dispatchRequestService.updateStatus(id, newStatus).pipe(map((response) => response.data!));
  }

  // ==================== Configuración de UI ====================

  getTableColumns(): TableColumn[] {
    return [
      {
        field: 'itemCode',
        header: 'Item Code',
        width: '150px',
        type: 'text',
        sortable: true,
      },
      { field: 'itemDescription', header: 'Item Description', width: '250px', type: 'text' },
      {
        field: 'quantity',
        header: 'DR Quantity',
        width: '120px',
        type: 'numeric',
        editable: true,
      },
      {
        field: 'uom',
        header: 'UOM',
        width: '100px',
        type: 'text',
      },
      {
        field: 'sourceLocations',
        header: 'Source Locations',
        type: 'locations',
        editable: false, // Auto-asignadas, no editables
      },
      {
        field: 'supplyRequestComment',
        header: 'SR Comment',
        width: '200px',
        type: 'text',
        editable: false, // Siempre readonly - heredado de Supply Request
      },
      {
        field: 'comments',
        header: 'DR Comments',
        width: '200px',
        type: 'text',
        editable: true,
      },
      {
        field: 'actions',
        header: 'Common Actions',
        width: '100px',
        type: 'actions',
      },
    ];
  }

  /**
   * Dispatch Request no tiene items de un iniciador
   * Retorna array vacío
   */
  getInitiatorItemsColumns(): TableColumn[] {
    return [];
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getAvailableActions(status: StatusDispatchRequest, document: DispatchRequest): TransactionAction[] {
    const actions: TransactionAction[] = [];

    switch (status) {
      case StatusDispatchRequest.Pending:
        actions.push({
          id: 'start-processing',
          label: 'Start Processing',
          icon: 'pi pi-play',
          severity: 'success',
          requiresConfirmation: true,
          confirmationMessage: 'Are you sure you want to start processing this Dispatch Request?',
        });
        actions.push({
          id: 'cancel',
          label: 'Cancel',
          icon: 'pi pi-times',
          severity: 'danger',
          requiresConfirmation: true,
          confirmationMessage: 'Are you sure you want to cancel this Dispatch Request?',
        });
        break;

      case StatusDispatchRequest.Processing:
        actions.push({
          id: 'complete',
          label: 'Complete',
          icon: 'pi pi-check',
          severity: 'success',
          requiresConfirmation: true,
          confirmationMessage: 'Are you sure you want to complete this Dispatch Request?',
        });
        // actions.push({
        //   id: 'back-to-pending',
        //   label: 'Back to Pending',
        //   icon: 'pi pi-refresh',
        //   severity: 'warn'
        // });
        break;

      case StatusDispatchRequest.Completed:
        // Solo visualización, no acciones de edición
        break;

      case StatusDispatchRequest.Cancelled:
        // actions.push({
        //   id: 'reopen',
        //   label: 'Reopen',
        //   icon: 'pi pi-replay',
        //   severity: 'warn',
        //   requiresConfirmation: true,
        //   confirmationMessage: 'Are you sure you want to reopen this Dispatch Request?'
        // });
        break;
    }

    return actions;
  }

  // ==================== Reglas de Negocio ====================

  canEditQuantities(status: StatusDispatchRequest): boolean {
    return status === StatusDispatchRequest.Pending || status === StatusDispatchRequest.Processing;
  }

  canEditLines(status: StatusDispatchRequest): boolean {
    return status === StatusDispatchRequest.Pending || status === StatusDispatchRequest.Processing;
  }

  validateForStatusChange(document: DispatchRequest, newStatus: StatusDispatchRequest): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validar que tenga entries
    if (!document.entries || document.entries.length === 0) {
      errors.push('Dispatch Request must have at least one entry');
    }

    // Validar que todas las entries tengan cantidad > 0
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    document.entries?.forEach((entry: any, index: number) => {
      if (entry.quantity <= 0) {
        errors.push(`DR_Error_Invalid_Quantity_Entry_${index + 1}`);
      }
    });

    // Si cambia a Processing o Completed, validar ubicaciones de origen
    if (newStatus === StatusDispatchRequest.Processing || newStatus === StatusDispatchRequest.Completed) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      document.entries?.forEach((entry: any, index: number) => {
        if (!entry.sourceLocations || entry.sourceLocations.length === 0) {
          errors.push(`DR_Error_No_Source_Locations_Entry_${index + 1}`);
        } else {
          // Validar que la suma de sourceLocations = cantidad
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const totalInLocations = entry.sourceLocations.reduce((sum: number, loc: any) => sum + loc.quantity, 0);
          const diff = Math.abs(totalInLocations - entry.quantity);
          if (diff > 0.001) {
            // Tolerancia para decimales
            errors.push(`DR_Error_Location_Mismatch_Entry_${index + 1}`);
          }
        }
      });
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  // ==================== Helpers ====================

  getDocumentTitle(document: DispatchRequest | null): string {
    if (!document) return 'Dispatch Request';
    return document.number || 'Dispatch Request';
  }

  getInitiatorEntityKey(dispatchRequestId: string): EntityKeyByReference {
    return {
      reference: dispatchRequestId,
      entityDefinitionCode: GlobalEntityDefinition.DispatchRequest,
    };
  }

  // ==================== Report Download ====================

  override supportsReportDownload(): boolean {
    return true;
  }

  override canDownloadReport(status: StatusDispatchRequest): boolean {
    return status === StatusDispatchRequest.Processing || status === StatusDispatchRequest.Completed;
  }

  override downloadReport(documentId: string): Observable<Blob> {
    return this.dispatchRequestService.downloadReport(documentId);
  }
}
